import { FC, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchUnits } from "../../store/unitsStore/unitsThunk";
import { fetchSkuVariants } from "../../store/skuVariantStore/skuVariantThunk";

// SKU "Variants" section (loose-sale portions, e.g. 30ml/60ml/90ml pegs).
// - A flag enables variants for the item.
// - When enabled, the user picks a set from the SkuVariant MASTER to prefill
//   the rows. The picked set is only a template: rows are COPIED into the SKU's
//   own list (no reference kept), so they can be freely edited per item.
// - Each row: name + unit + qty + a sale price with a Flat (₹) / Percent (%) type.
export interface VariantRowValue {
  name: string;
  unit: string;
  qty: number | string;
  salePriceType: "flat" | "percent";
  percentBasis: "mrp" | "purchase" | "sale"; // which base price the % applies to
  salePrice: number | string;
}

export interface SkuVariantsValue {
  variantsEnabled: boolean;
  variants: VariantRowValue[];
}

interface Props {
  value?: { variantsEnabled?: boolean; variants?: any[] };
  onChange: (val: SkuVariantsValue) => void;
}

const emptyRow: VariantRowValue = { name: "", unit: "", qty: "", salePriceType: "flat", percentBasis: "mrp", salePrice: "" };

const SkuVariantsSection: FC<Props> = ({ value, onChange }) => {
  const dispatch = useAppDispatch();
  const { units } = useAppSelector((state) => state.units);
  const { variants: variantSets } = useAppSelector((state) => state.skuVariants);

  const [enabled, setEnabled] = useState(false);
  const [selectedSetId, setSelectedSetId] = useState<string>("");
  const [rows, setRows] = useState<VariantRowValue[]>([]);

  useEffect(() => {
    if (!units || units.length === 0) dispatch(fetchUnits());
    if (!variantSets || variantSets.length === 0) dispatch(fetchSkuVariants());
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sync from the incoming value WHENEVER it changes — on edit load, on switching
  // to a different item, and on reset. Syncing to empty is intentional and critical:
  // it clears the PREVIOUS item's variants so they can never bleed into (or be saved
  // onto) an item that has none. `value` only changes on load/reset/item-switch (never
  // during editing, which drives `rows`/`enabled`), so this never clobbers live edits.
  useEffect(() => {
    setEnabled(!!value?.variantsEnabled);
    setRows(
      (value?.variants || []).map((v: any) => ({
        name: v.name || "",
        unit: v.unit?._id || v.unit || "",
        qty: v.qty ?? "",
        salePriceType: v.salePriceType === "percent" ? "percent" : "flat",
        percentBasis: ["mrp", "purchase", "sale"].includes(v.percentBasis) ? v.percentBasis : "mrp",
        salePrice: v.salePrice ?? "",
      }))
    );
    setSelectedSetId("");
  }, [value]);

  // Emit changes upward.
  useEffect(() => {
    onChange({
      variantsEnabled: enabled,
      variants: enabled
        ? rows
            .filter((r) => r.name && r.unit)
            .map((r) => ({
              name: r.name,
              unit: r.unit,
              qty: r.qty === "" ? 0 : Number(r.qty),
              salePriceType: r.salePriceType,
              percentBasis: r.percentBasis,
              salePrice: r.salePrice === "" ? 0 : Number(r.salePrice),
            }))
        : [],
    });
  }, [enabled, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const unitOptions = useMemo(
    () => (units || []).map((u: any) => ({ label: `${u.name}${u.code ? ` (${u.code})` : ""}`, value: u._id })),
    [units]
  );
  const setOptions = useMemo(
    () => (variantSets || []).map((s: any) => ({ label: s.name, value: s._id })),
    [variantSets]
  );

  const setRow = (idx: number, patch: Partial<VariantRowValue>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, { ...emptyRow }]);
  const removeRow = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));

  // Copy the picked master set's rows into the SKU's own editable list.
  const onPickSet = (setId: string) => {
    setSelectedSetId(setId);
    const set = (variantSets || []).find((s: any) => s._id === setId);
    if (!set) return;
    setRows(
      (set.variants || []).map((v: any) => ({
        name: v.name || "",
        unit: v.unit?._id || v.unit || "",
        qty: v.qty ?? "",
        salePriceType: "flat",
        percentBasis: "mrp",
        salePrice: "",
      }))
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormControlLabel
        control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
        label="Enable variants for this item"
      />

      {enabled && (
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={setOptions}
                getOptionLabel={(o: any) => o.label || ""}
                isOptionEqualToValue={(o: any, v: any) => o.value === v.value}
                value={setOptions.find((o) => o.value === selectedSetId) || null}
                onChange={(_e, v: any) => onPickSet(v ? v.value : "")}
                renderInput={(params) => (
                  <TextField {...params} label="Load from variant set (optional)" size="small" helperText="Prefills the rows below; you can edit them freely" />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 1.5 }}>
            {rows.map((row, i) => (
              <Grid container spacing={1} key={i} sx={{ mb: 1, alignItems: "center" }}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name"
                    placeholder="e.g. Chota Peg"
                    value={row.name}
                    onChange={(e) => setRow(i, { name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Autocomplete
                    options={unitOptions}
                    getOptionLabel={(o: any) => o.label || ""}
                    isOptionEqualToValue={(o: any, v: any) => o.value === v.value}
                    value={unitOptions.find((o) => o.value === row.unit) || null}
                    onChange={(_e, v: any) => setRow(i, { unit: v ? v.value : "" })}
                    renderInput={(params) => <TextField {...params} label="Unit" size="small" />}
                  />
                </Grid>
                <Grid item xs={6} sm={1}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Qty"
                    value={row.qty}
                    onChange={(e) => setRow(i, { qty: e.target.value })}
                  />
                </Grid>
                <Grid item xs={8} sm={row.salePriceType === "percent" ? 3 : 5}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Sale Price"
                    value={row.salePrice}
                    onChange={(e) => setRow(i, { salePrice: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{row.salePriceType === "percent" ? "%" : "₹"}</InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Select
                            value={row.salePriceType}
                            onChange={(e) => setRow(i, { salePriceType: e.target.value as "flat" | "percent" })}
                            variant="standard"
                            disableUnderline
                            sx={{ fontSize: 13 }}
                          >
                            <MenuItem value="flat">Flat ₹</MenuItem>
                            <MenuItem value="percent">Percent %</MenuItem>
                          </Select>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {/* % basis — only when the row is percent-priced. Resolved against the
                    sold batch (fallback SKU master) at sale time. Omitted for flat rows
                    so Sale Price takes the freed width. */}
                {row.salePriceType === "percent" && (
                  <Grid item xs={4} sm={2}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="% of"
                      value={row.percentBasis}
                      onChange={(e) => setRow(i, { percentBasis: e.target.value as "mrp" | "purchase" | "sale" })}
                    >
                      <MenuItem value="mrp">MRP</MenuItem>
                      <MenuItem value="purchase">Purchase Price</MenuItem>
                      <MenuItem value="sale">Sale Price</MenuItem>
                    </TextField>
                  </Grid>
                )}
                <Grid item xs={12} sm={1} sx={{ display: "flex", justifyContent: "flex-end", pr: 0 }}>
                  <IconButton size="small" color="error" onClick={() => removeRow(i)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Button startIcon={<AddIcon />} size="small" onClick={addRow} sx={{ mt: 0.5 }}>
              Add variant
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SkuVariantsSection;
